import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import DynamicCoordinates from "@/components/DynamicCoordinates";
import aboutImg from "@/assets/about-hero-bg.png";

const ABOUT_IMG = aboutImg;

const TIMELINE = [
  { year: "2026", title: "Lab founded", body: "Avinya was established by a collective of product architects and expert engineers to bridge the gap between academic research and industrial-scale deployment. Our mandate: to build a globally scalable technology group capable of architecting the post-cloud era." },
  { year: "2026", title: "Agentic AI", body: "Following the initiation of our internal agentic research program, we have successfully begun building production grade agents. These systems move beyond basic automation into true reasoning-based autonomy, solving complex, non-linear workflows for modern enterprises." },
  { year: "2026", title: "India ops", body: "Operating as a high-performance, decentralized network across India, we have officially onboarded our first enterprise clients. By integrating specialized infrastructure into core business operations, we are proving our remote-first, product-centric egineering model." },
];

const VALUES = [
  {
    code: "V.01",
    title: "Global-scale deployments",
    body: "We architect systems engineered for the entire planet. From edge-nodes to core infrastructure, our designs prioritize resilience and high-availability across global networks, ensuring software operates reliably everywhere.",
  },
  {
    code: "V.02",
    title: "Build for Excellence",
    body: "We view the craft of code as a rigorous discipline. We commit to unparalleled engineering standards, rigorously testing and refining our architectures to deliver elegant, robust, and industry-leading technical solutions.",
  },
  {
    code: "V.03",
    title: "Shared Technical Literacy",
    body: "We believe complex technology should not be a walled garden. We actively work to democratize knowledge by translating deep-tech concepts into transparent, accessible blueprints for the broader community.",
  },
  {
    code: "V.04",
    title: "Infrastructure for Good",
    body: "Our primary mandate is to develop systems that benefit humanity. Whether it's ethical autonomous agents or resilient public telemetry, we build foundational technology that advances equitable outcomes and global safety.",
  },
];

export default function About() {
  return (
    <div className="fade-up" data-testid="page-about">
      {/* Header */}
      <section className="relative pt-28 pb-20 border-b border-[#1E2BFF]/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="overline mb-5">// ABOUT.LAB</div>
            <h1 className="font-hero text-5xl lg:text-8xl font-black uppercase tracking-tighter text-white leading-[0.95]">
              A research <br />
              <span className="text-[#00F0FF] glow-cyan">studio,</span> not <br />
              an agency.
            </h1>
          </div>
          <div className="lg:col-span-5 lg:pt-12">
            <p className="text-[#C9CDE9] text-lg leading-relaxed">
              Avinya Research Labs is a team of engineers, designers, and researchers building software that operates the real world. We believe the greatest challenges of our time—from decentralized intelligence to global telemetry—cannot be solved in silos.
            </p>
            <p className="mt-6 text-[#9BA1D6] text-base leading-relaxed">
              We exist as a collaborative nexus. By bringing together the deep-tech curiosity of academia with the high-stakes requirements of industry, we deliver high-impact solutions built with operational lean-ness and global scalability in mind.
            </p>
          </div>
        </div>
      </section>

      {/* Imagery band */}
      <section className="relative h-[50vh] overflow-hidden border-b border-[#1E2BFF]/20">
        <img
          src={ABOUT_IMG}
          alt="Lab"
          className="absolute inset-0 w-full h-full object-cover opacity-60 about-img"
        />
        <div className="absolute inset-0 about-image-overlay" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-10 pb-40">
            <div className="flex items-end justify-between gap-6 font-mono text-[10px] tracking-[0.3em] uppercase text-[#9BA1D6]">
              <div>LAB.07 / RESIDENCY ROAD</div>
              <div>PAN INDIA</div>
              <div className="hidden md:block"><DynamicCoordinates /></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
            <div>
              <div className="overline mb-4">// DOCTRINE.INDEX</div>
              <h2 className="font-display text-3xl lg:text-5xl font-bold text-white tracking-tighter">
                Operating <span className="text-[#00F0FF] glow-cyan">principles.</span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.code}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="hud-card corner-brackets tilt-card scan-sweep p-8"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="overline">// {v.code}</div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#9BA1D6]">
                    0{i + 1} / 0{VALUES.length}
                  </div>
                </div>
                <h3 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-white">
                  {v.title}
                </h3>
                <p className="mt-4 text-[#9BA1D6] leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="relative py-24 lg:py-32 border-t border-[#1E2BFF]/20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="overline mb-4">// LOG.TIMELINE</div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-white tracking-tighter mb-14">
            Lab <span className="text-[#00F0FF] glow-cyan">dispatches.</span>
          </h2>
          <div className="relative">
            <div className="absolute left-4 lg:left-24 top-0 bottom-0 w-px bg-[#1E2BFF]/30" />
            <ul className="space-y-10">
              {TIMELINE.map((t, i) => (
                <motion.li
                  key={t.year}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  className="relative pl-16 lg:pl-40"
                >
                  <div className="absolute left-0 lg:left-[66px] top-2 w-8 h-8 border border-[#00F0FF] flex items-center justify-center">
                    <span className="pulse-dot" />
                  </div>
                  <div className="absolute left-0 lg:left-0 top-1 font-mono text-[11px] tracking-[0.25em] uppercase text-[#00F0FF] hidden lg:block">
                    // {t.year}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#00F0FF] lg:hidden mb-2">
                    // {t.year}
                  </div>
                  <h3 className="font-display text-2xl lg:text-3xl font-bold text-white tracking-tight">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-[#9BA1D6] leading-relaxed max-w-2xl">
                    {t.body}
                  </p>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="hud-card corner-brackets p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <h3 className="font-display text-3xl lg:text-5xl font-bold text-white tracking-tighter">
              Come build at <span className="text-[#00F0FF] glow-cyan">the edge.</span>
            </h3>
            <div className="flex gap-3">
              <Link to="/careers" className="btn-ghost" data-testid="about-cta-careers">
                Open Roles
              </Link>
              <Link to="/contact" className="btn-primary" data-testid="about-cta-contact">
                Contact <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
